import { arrayOfAll } from 'neuro-utils';
import { dmdClinicalArchiveTypes, dmdDocumentTypes } from './dmd';
import { epilepsyClinicalArchiveTypes, epilepsyDocumentTypes } from './epilepsy';
import { huntingtonDocumentTypes } from './huntington';
import { mgravisDocumentTypes } from './mgravis';
import { msDocumentTypes } from './ms';
import { ninmtClinicalArchiveTypes, ninmtDocumentTypes } from './ninmt';
import { parkinsonClinicalArchiveTypes, parkinsonDocumentTypes } from './parkinson';
import { sleepApneaClinicalArchiveTypes, sleepApneaDocumentTypes } from './sleepApnea';
import { smaClinicalArchiveTypes, smaDocumentTypes } from './sma';
import { polyneuroDocumentTypes } from './polyneuro';

import type { DocTypesByName, UsedDocTypes } from './utils';
import type { LoadableDocConfig } from './utils/index';

/** All clinical archive document types supported by Neuro. */
export type ClinicalArchive = 'NEU' | 'HOI' | 'LNEU' | 'TOI' | 'FYST' | 'KEU' | 'RADU';

/** Platforms mapped to their respective clinical archive doc types. */
export const PlatformToArchiveMap: { [key in Platform]: readonly ClinicalArchive[] } = {
  sma: smaClinicalArchiveTypes,
  dmd: dmdClinicalArchiveTypes,
  epilepsy: epilepsyClinicalArchiveTypes,
  huntington: [],
  ms: [],
  parkinson: parkinsonClinicalArchiveTypes,
  sleepApnea: sleepApneaClinicalArchiveTypes,
  ninmt: ninmtClinicalArchiveTypes,
  mgravis: [],
  polyneuro: [],
} as const;

export const documentTypesByPlatform = {
  dmd: dmdDocumentTypes,
  epilepsy: epilepsyDocumentTypes,
  huntington: huntingtonDocumentTypes,
  mgravis: mgravisDocumentTypes,
  ms: msDocumentTypes,
  ninmt: ninmtDocumentTypes,
  parkinson: parkinsonDocumentTypes,
  sleepApnea: sleepApneaDocumentTypes,
  sma: smaDocumentTypes,
  polyneuro: polyneuroDocumentTypes,
} satisfies Record<Platform, readonly LoadableDocConfig[]>;

/** Every platform's document configs as one union. */
type AnyPlatformDocConfig = (typeof documentTypesByPlatform)[Platform];

/**
 * Each unique page/section `name` used by any platform, mapped to the document types that page uses
 * (its own name when that is a document type, plus its `subTypes` and `secondaryDocs`).
 * Names shared by several platforms get the union of all of their document types.
 */
export type DocTypesByPageName = DocTypesByName<AnyPlatformDocConfig>;

/** Same as {@link DocTypesByPageName}, but kept separate per platform. */
export type DocTypesByPlatformAndPageName = {
  [P in Platform]: DocTypesByName<(typeof documentTypesByPlatform)[P]>;
};

/** Every document type reachable through any platform's document config. */
export type UsedPlatformDocTypes = UsedDocTypes<AnyPlatformDocConfig>;

/** All platforms in Neuro. */
export const platforms = arrayOfAll.options<Platform>()([
  'sma',
  'dmd',
  'parkinson',
  'ms',
  'huntington',
  'epilepsy',
  'sleepApnea',
  'ninmt',
  'mgravis',
  'polyneuro',
]);
